import {
  expandRankSelection,
  lineKeyOf,
  type CommandEntryInputDto,
  type CommandRoleDto,
  type RequirementLineInputDto,
  type SharedColumnInputDto,
} from '@pams/shared';
import { commandAskIsReal } from '@/pages/ArrangementBuilder/autosave';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §2] ONE PERSON, MANY DUTY POINTS — THE BUILDER'S HALF OF A SHARED COLUMN. PURE.
 *
 *  A shared column is ONE requirement line on its ANCHOR card; every other card carries none, and
 *  keeps its own number for the column in a STASH so switching sharing off gives it back (§2e).
 *  The server asserts the same shape on every write (`applyArrangementContent`); this file is what
 *  keeps the form in that shape between writes, so an autosave never sends a document the server
 *  would refuse.
 *
 *  Four operations, and every card write on the page goes through the last one:
 *
 *    `enableSharing`    converge every card on the anchor's policy version, stash each other card's
 *                       own number for the column and remove its line. REFUSES, stating the count,
 *                       when converging would drop named supervisors (§2f); otherwise reports every
 *                       force column a card loses, with its count, for the confirmation to state.
 *    `disableSharing`   give every stash back as a line, in place; a card with no stash (added after
 *                       sharing began) gets the column at zero where its version offers it.
 *    `removeCard`       §2g: deleting the anchor ENDS sharing for its columns and restores every
 *                       other card's own number. The anchor is never re-homed by position.
 *    `normalizeSharing` the safety net: an entry whose anchor has gone is ended, the column seeded
 *                       or copied onto a covered card is removed, orphaned stash rows are pruned.
 *
 *  ⚠️ THE ANCHOR IS A CARD ID, NEVER A POSITION. A drag rebuilds the array by id, so the anchor
 *  cannot move with it — `DutyPointStack`'s own rule, applied to one more reference.
 *
 *  ⚠️ NOTHING HERE PINS A POLICY VERSION. Converging copies the anchor's `(policyId,
 *  policyVersionId)` exactly as they stand, `undefined` included, so an unpinned anchor keeps asking
 *  the SERVER which version is current ([PRE-P6 §3a]) and every card gets the same answer in one save.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** The part of a card this file reads and writes — `DutyPointFormValue` satisfies it. */
export type SharingCard = {
  id: string;
  policyId?: string;
  policyVersionId?: string;
  columnsFromVersionId?: string;
  requirementLines: RequirementLineInputDto[];
};

/** Lines a card's binding version offers, at zero — the page's `seededLines`, per card. */
export type ZeroLineFor = (card: SharingCard, lineKey: string) => RequirementLineInputDto | undefined;

export type SharingState<T extends SharingCard> = { cards: T[]; shared: SharedColumnInputDto[] };

/** What enabling a share will do, for the confirmation to state before anything is written. */
export type SharingReport = {
  /** Cards moved onto the anchor's policy, and every force column each loses with its count. */
  converged: { cardId: string; dropped: { name: string; requiredCount: number }[] }[];
  /** Each covered card's own number for the column, set aside to be given back later. */
  setAside: { cardId: string; requiredCount: number }[];
};

const keyOf = (line: RequirementLineInputDto): string => lineKeyOf(line);

/**
 * ⚠️ A CARD, AS OPPOSED TO RESIDUE. rc-field-form re-materialises a removed card's index as an
 * object with no `id` and no lines (`toDraft`'s own filter says why), and the page hands this
 * file the raw store. Residue is passed through untouched and never counted — a ghost that
 * counted towards "two cards" would keep a share alive on a one-card order.
 */
const isCard = (card: SharingCard | undefined): boolean =>
  typeof card?.id === 'string' && card.id.length > 0 && Array.isArray(card.requirementLines);

/** The two fields every reader of a share needs — the watched draft carries these and no stash. */
export type SharingEntry = Pick<SharedColumnInputDto, 'lineKey' | 'anchorDutyPointId'>;

/** The keys a card is COVERED for — anchored on some other card. */
export const coveringKeys = (shared: readonly SharingEntry[], cardId: string): Set<string> =>
  new Set(shared.filter((entry) => entry.anchorDutyPointId !== cardId).map((entry) => entry.lineKey));

/**
 * True when a card's force is covered from another card — so a reader must not say it "asks for no
 * force". The preview modal, the record view and the fill screen read the same answer.
 */
export const coveredElsewhere = (shared: readonly SharingEntry[], dutyPointId: string): boolean =>
  shared.some((entry) => entry.anchorDutyPointId !== dutyPointId);

/** The keys a card ANCHORS. */
export const anchoredKeys = (shared: readonly SharingEntry[], cardId: string): Set<string> =>
  new Set(shared.filter((entry) => entry.anchorDutyPointId === cardId).map((entry) => entry.lineKey));

/**
 * ⚠️ `submittableLines`' RULE, ON THE STASH TOO — AND WITHOUT IT ONE UNPICKED ROW LOCKS THE FORM.
 *
 * `fromGrid` writes an allocation per source row EVEN AT ZERO and even before a division is chosen,
 * so a row being typed into survives the round trip. A stash copied straight from those lines would
 * carry `divisionOrgUnitId: ''`, the schema refuses it as an ObjectId, and EVERY later autosave would
 * 422 until sharing was switched off — for a row the operator never finished.
 */
export const keptAllocations = (
  allocations: RequirementLineInputDto['allocations'],
): RequirementLineInputDto['allocations'] =>
  allocations.filter((allocation) => allocation.count > 0 && allocation.divisionOrgUnitId.trim().length > 0);

/** The stash rows as they may go on the wire. */
export const submittableSharedColumns = (shared: readonly SharedColumnInputDto[]): SharedColumnInputDto[] =>
  shared.map((entry) => ({
    ...entry,
    stashed: entry.stashed.map((row) => ({ ...row, allocations: keptAllocations(row.allocations) })),
  }));

/**
 * ⚠️ A CARD MOVED ONTO ANOTHER VERSION KEEPS EVERY NUMBER WHOSE COLUMN SURVIVES — `useCurrentColumns`'
 * rule. The new version's columns come first in its own order, one-off lines (the ORDER's, never the
 * policy's) follow untouched, and what the new version does not carry is DROPPED and reported.
 */
const convergedLines = (
  lines: readonly RequirementLineInputDto[],
  anchorColumns: readonly RequirementLineInputDto[],
  nameOf: (lineKey: string) => string,
): { lines: RequirementLineInputDto[]; dropped: { name: string; requiredCount: number }[] } => {
  const offered = new Set(anchorColumns.map(keyOf));
  const kept = new Map(lines.filter((line) => !line.adHocLabel).map((line) => [keyOf(line), line]));
  return {
    lines: [
      ...anchorColumns.map((column) => {
        const previous = kept.get(keyOf(column));
        return previous
          ? { ...column, requiredCount: previous.requiredCount, allocations: previous.allocations }
          : column;
      }),
      ...lines.filter((line) => line.adHocLabel),
    ],
    dropped: lines
      .filter((line) => !line.adHocLabel && !offered.has(keyOf(line)))
      .map((line) => ({ name: nameOf(keyOf(line)), requiredCount: line.requiredCount })),
  };
};

/**
 * Share `lineKey` from `anchorId` across every other card.
 *
 * `seatsLost(card)` is how many named supervisors converging THAT card would lose — seats whose role
 * the anchor's version does not carry, or whose officer's rank its roster refuses. The page answers
 * it because only the page holds the rosters and the rank master.
 */
export const enableSharing = <T extends SharingCard>(input: {
  cards: readonly T[];
  shared: readonly SharedColumnInputDto[];
  anchorId: string;
  lineKey: string;
  /** The anchor's binding version's own columns, seeded at zero, in the version's order. */
  anchorColumns: readonly RequirementLineInputDto[];
  nameOf: (lineKey: string) => string;
  seatsLost: (card: T) => number;
  /**
   * ⚠️ [C6a §2] THE ROLES THIS CARD ASKS A DIVISION FOR THAT CONVERGING IT WOULD ORPHAN — as labels,
   * never a number. Optional, so every existing caller is unchanged; the page passes
   * `asksLostOnConverge`. A lost ask refuses exactly as a lost named seat does.
   */
  asksLost?: (card: T) => readonly string[];
}):
  | ({ ok: true; report: SharingReport } & SharingState<T>)
  | { ok: false; lostSeats: { cardId: string; count: number; asked?: readonly string[] }[] } => {
  const anchor = input.cards.find((card) => card.id === input.anchorId);
  if (!anchor || !anchor.requirementLines.some((line) => keyOf(line) === input.lineKey)) {
    return { ok: false, lostSeats: [] };
  }

  const differs = (card: T): boolean =>
    isCard(card) &&
    card.id !== anchor.id &&
    (card.policyId !== anchor.policyId || card.policyVersionId !== anchor.policyVersionId);

  const lostSeats = input.cards
    .filter(differs)
    .map((card) => {
      const asked = input.asksLost?.(card) ?? [];
      /* The key only when something is asked, so a named-seats-only refusal reads exactly as before. */
      return { cardId: card.id, count: input.seatsLost(card), ...(asked[0] !== undefined ? { asked } : {}) };
    })
    .filter((row) => row.count > 0 || row.asked !== undefined);
  if (lostSeats[0] !== undefined) return { ok: false, lostSeats };

  const report: SharingReport = { converged: [], setAside: [] };
  const entry: SharedColumnInputDto = { lineKey: input.lineKey, anchorDutyPointId: anchor.id, stashed: [] };

  const cards = input.cards.map((card) => {
    if (!isCard(card) || card.id === anchor.id) return card;
    let next = card;
    if (differs(card)) {
      const moved = convergedLines(card.requirementLines, input.anchorColumns, input.nameOf);
      report.converged.push({ cardId: card.id, dropped: moved.dropped });
      next = {
        ...card,
        policyId: anchor.policyId,
        policyVersionId: anchor.policyVersionId,
        columnsFromVersionId: anchor.columnsFromVersionId,
        requirementLines: moved.lines,
      };
    }
    const index = next.requirementLines.findIndex((line) => keyOf(line) === input.lineKey);
    const line = next.requirementLines[index];
    if (line) {
      entry.stashed.push({
        dutyPointId: card.id,
        requiredCount: line.requiredCount,
        displayOrder: index,
        restrictToPostTypeIds: [...(line.restrictToPostTypeIds ?? [])],
        allocations: keptAllocations(line.allocations),
      });
      report.setAside.push({ cardId: card.id, requiredCount: line.requiredCount });
      next = { ...next, requirementLines: next.requirementLines.filter((_row, at) => at !== index) };
    }
    return next;
  });

  return { ok: true, report, cards, shared: [...input.shared, entry] };
};

/**
 * Stop sharing `lineKey`: every stash back as a line, in place, and a zero line where a card has no
 * stash but its version offers the column. The anchor keeps its own line exactly as it is.
 */
export const disableSharing = <T extends SharingCard>(input: {
  cards: readonly T[];
  shared: readonly SharedColumnInputDto[];
  lineKey: string;
  zeroLineFor: ZeroLineFor;
}): SharingState<T> => {
  const entry = input.shared.find((row) => row.lineKey === input.lineKey);
  if (!entry) return { cards: [...input.cards], shared: [...input.shared] };

  const anchor = input.cards.find((card) => card.id === entry.anchorDutyPointId);
  const identity = anchor?.requirementLines.find((line) => keyOf(line) === input.lineKey);

  const cards = input.cards.map((card) => {
    if (!isCard(card) || card.id === entry.anchorDutyPointId) return card;
    if (card.requirementLines.some((line) => keyOf(line) === input.lineKey)) return card;
    const stash = entry.stashed.find((row) => row.dutyPointId === card.id);
    /*
     * ⚠️ WHAT THE COLUMN IS: the anchor's own line, or — when the anchor has lost it — the column as
     * this card's OWN version offers it. Neither means the version no longer carries a policy column
     * (or the column was a one-off whose label went with its line), and a line restored then would be
     * a column the server refuses; that stash is the one loss `normalizeSharing`'s header states.
     */
    const base = identity ?? input.zeroLineFor(card, input.lineKey);
    if (!base) return card;
    const restored: RequirementLineInputDto | undefined = stash
      ? {
          postTypeId: base.postTypeId ?? null,
          vehicleTypeId: base.vehicleTypeId ?? null,
          adHocLabel: base.adHocLabel ?? null,
          restrictToPostTypeIds: [...(stash.restrictToPostTypeIds ?? [])],
          requiredCount: stash.requiredCount,
          allocations: stash.allocations.map((allocation) => ({ ...allocation })),
        }
      : base.adHocLabel
        ? undefined
        : input.zeroLineFor(card, input.lineKey);
    if (!restored) return card;
    const at = Math.min(stash?.displayOrder ?? card.requirementLines.length, card.requirementLines.length);
    return {
      ...card,
      requirementLines: [...card.requirementLines.slice(0, at), restored, ...card.requirementLines.slice(at)],
    };
  });

  return { cards, shared: input.shared.filter((row) => row.lineKey !== input.lineKey) };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ §2g — DELETING THE ANCHOR ENDS SHARING FOR EVERY COLUMN IT ANCHORED, AND GIVES THE OTHER
 *  CARDS THEIR OWN NUMBERS BACK. It is never re-homed onto the next card: "the first one" is a
 *  position, and a column that silently moved to whichever card slid into slot one would ask
 *  for an inspector at a place the operator never chose.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const removeCard = <T extends SharingCard>(input: {
  cards: readonly T[];
  shared: readonly SharedColumnInputDto[];
  cardId: string;
  zeroLineFor: ZeroLineFor;
}): SharingState<T> => {
  let state: SharingState<T> = { cards: [...input.cards], shared: [...input.shared] };
  for (const lineKey of anchoredKeys(input.shared, input.cardId)) {
    state = disableSharing({ ...state, lineKey, zeroLineFor: input.zeroLineFor });
  }
  return normalizeSharing({
    cards: state.cards.filter((card) => card.id !== input.cardId),
    shared: state.shared,
    zeroLineFor: input.zeroLineFor,
  });
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE SAFETY NET EVERY CARD WRITE PASSES THROUGH — AND WHAT IT MAY DO SILENTLY.
 *
 *  ⚠️ IT NEVER DISCARDS A NUMBER THE OPERATOR TYPED. Each branch is either a restore or a removal of
 *  something that was never typed:
 *
 *    · fewer than two cards, or an entry whose anchor has gone: sharing ends and every stash is
 *      given back (`disableSharing`);
 *    · the column on a COVERED card: only ever a COPY (a duplicated anchor) or a SEED (a covered
 *      card's grid seeded from its version) — typing into a covered column is impossible, because
 *      a covered card renders a dash there — so it is removed;
 *    · a stash row for a card no longer on the order, or for the anchor: pruned, as the server does.
 *
 *  The one loss it cannot avoid is an entry whose anchor no longer carries the column AND whose
 *  column cannot be recovered — a one-off label is its identity, and the page routes that removal
 *  through `disableSharing` before the line goes, so this branch is a backstop rather than a path.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const normalizeSharing = <T extends SharingCard>(input: {
  cards: readonly T[];
  shared: readonly SharedColumnInputDto[];
  zeroLineFor: ZeroLineFor;
}): SharingState<T> => {
  let state: SharingState<T> = { cards: [...input.cards], shared: [...input.shared] };
  if (state.shared[0] === undefined) return state;

  const realCount = state.cards.filter(isCard).length;
  for (const entry of input.shared) {
    const anchor = state.cards.find((card) => isCard(card) && card.id === entry.anchorDutyPointId);
    const anchorHasLine = anchor?.requirementLines.some((line) => keyOf(line) === entry.lineKey) ?? false;
    if (realCount < 2 || !anchor || !anchorHasLine) {
      state = disableSharing({ ...state, lineKey: entry.lineKey, zeroLineFor: input.zeroLineFor });
    }
  }

  const ids = new Set(state.cards.filter(isCard).map((card) => card.id));
  const cards = state.cards.map((card) => {
    if (!isCard(card)) return card;
    const covered = coveringKeys(state.shared, card.id);
    if (covered.size === 0 || !card.requirementLines.some((line) => covered.has(keyOf(line)))) return card;
    return { ...card, requirementLines: card.requirementLines.filter((line) => !covered.has(keyOf(line))) };
  });
  const shared = state.shared.map((entry) => ({
    ...entry,
    stashed: entry.stashed.filter((row) => ids.has(row.dutyPointId) && row.dutyPointId !== entry.anchorDutyPointId),
  }));
  return { cards, shared };
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ §2f — HOW MANY NAMED SUPERVISORS A CARD WOULD LOSE BY MOVING ONTO THE ANCHOR'S VERSION.
 *
 *  Counted in PEOPLE, never rows — one officer seated on three shifts is one person
 *  (`orderSheet.service.ts`' rule). A seat is lost when:
 *
 *    · its role is not on the anchor's version — `CommandSection` builds rows from the bound
 *      version's roles only, so the entry would go on being SENT while nothing renders it, and
 *      the server then refuses the key; or
 *    · its role is there, but restricted to ranks that do not include the officer's.
 *
 *  A ONE-OFF seat (`adHocLabel`) is the card's own vocabulary and survives any version.
 *
 *  ⚠️ AN OFFICER WHOSE RANK THE PAGE CANNOT SEE — named in this sitting, so not yet on the
 *  record — counts as lost unless the anchor's role accepts every rank the card's own role did.
 *  That is the fail-safe direction: the operator is told to clear a seat that might have been
 *  kept, never told nothing is lost about a seat the next save would refuse.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const seatsLostOnConverge = (input: {
  entries: readonly CommandEntryInputDto[];
  /** The card's own version's roles. */
  fromRoles: readonly CommandRoleDto[];
  /** The anchor's version's roles. */
  toRoles: readonly CommandRoleDto[];
  /** An officer's rank id, when the page knows it. */
  rankOf: (officerId: string) => string | undefined;
  rankIdByCode: ReadonlyMap<string, string>;
}): number => {
  /** `null` is "any rank" — an empty allow-list means exactly that. */
  const eligible = (role: CommandRoleDto): Set<string> | null => {
    const ids = expandRankSelection(
      { rankIds: role.eligibleRankIds, groupKeys: role.eligibleRankGroupKeys },
      input.rankIdByCode,
    );
    return ids.length === 0 ? null : new Set(ids);
  };
  const lost = new Set<string>();
  for (const entry of input.entries) {
    if (!entry.roleKey) continue;
    const officers = [...entry.officerIds, ...(entry.perSlot ?? []).flatMap((row) => row.officerIds)];
    const target = input.toRoles.find((role) => role.key === entry.roleKey);
    if (!target) {
      for (const id of officers) lost.add(id);
      continue;
    }
    const accepts = eligible(target);
    if (accepts === null) continue;
    const source = input.fromRoles.find((role) => role.key === entry.roleKey);
    const was = source ? eligible(source) : null;
    const widerOrEqual = was !== null && [...was].every((rank) => accepts.has(rank));
    for (const id of officers) {
      const rank = input.rankOf(id);
      if (rank === undefined ? !widerOrEqual : !accepts.has(rank)) lost.add(id);
    }
  }
  return lost.size;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C6a §2] THE OTHER HALF OF §2f — A ROLE THIS CARD ASKS A DIVISION FOR, WHICH THE ANCHOR'S
 *  VERSION DOES NOT CARRY.
 *
 *  `seatsLostOnConverge` counts PEOPLE; an ask names nobody yet, so it cannot be counted there
 *  without turning a command position into a number — and it must not be dropped silently either.
 *  Converged, the card renders the anchor version's roles only, so the ask would go on being SENT
 *  while nothing shows it, and publish then refuses the orphaned role. So the loss is STATED, by the
 *  role's label, and the page refuses the converge until it is removed.
 *
 *  A ONE-OFF role is the card's own vocabulary and survives any version, exactly as a named one-off
 *  seat does. Rank restrictions do not apply: an ask carries no officer to check yet.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const asksLostOnConverge = (input: {
  entries: readonly CommandEntryInputDto[];
  /** The card's own version's roles — where a lost role's LABEL comes from. */
  fromRoles: readonly CommandRoleDto[];
  /** The anchor's version's roles. */
  toRoles: readonly CommandRoleDto[];
}): string[] =>
  input.entries.flatMap((entry) => {
    const roleKey = entry.roleKey;
    if (!roleKey || !(entry.asks ?? []).some(commandAskIsReal)) return [];
    if (input.toRoles.some((role) => role.key === roleKey)) return [];
    return [input.fromRoles.find((role) => role.key === roleKey)?.label ?? roleKey];
  });
